/**
 * `pending-synthetic-monitoring` v1 — shared contracts.
 *
 * A monitor is a `test_schedules` row with `is_monitor = TRUE`.
 * The schemas here describe the monitor-specific config + the
 * status-event timeline + the REST surface.
 */

import { z } from 'zod';

export const MonitorStatusSchema = z.enum(['up', 'degraded', 'down', 'maintenance']);
export type MonitorStatus = z.infer<typeof MonitorStatusSchema>;

/**
 * `pending-synthetic-monitoring` v1.1 — per-channel page configuration.
 *
 * v1 only kept the enum of channel names on the monitor. v1.1 adds
 * per-channel target metadata so the monitor itself can carry, e.g.,
 * the PagerDuty integration key or the Slack incoming-webhook URL,
 * without the service having to fall back to org-wide admin defaults.
 *
 * Every field is optional — when a channel target is absent on the
 * monitor, the dispatcher falls back to the org-default in
 * `MonitorsAdminConfig`. When BOTH are absent the dispatcher logs +
 * skips that channel (best-effort; the in-app + email channels still
 * fire so on-call is never silently dropped).
 */
export const MonitorChannelTargetsSchema = z
  .object({
    /** PagerDuty Events API v2 integration key (a.k.a. routing key). */
    pagerdutyIntegrationKey: z.string().max(64).optional(),
    /**
     * Slack incoming-webhook URL. Must start with
     * `https://hooks.slack.com/`. Posted as a `chat.postMessage`-style
     * JSON body — no token required, the URL is the auth.
     */
    slackWebhookUrl: z
      .string()
      .url()
      .optional()
      .refine((u) => !u || u.startsWith('https://hooks.slack.com/'), {
        message: 'slackWebhookUrl must start with https://hooks.slack.com/',
      }),
    /**
     * Generic outbound webhook. Receives a JSON body documented in
     * `docs/handbook/features/completed/monitor-status-page.md` (event
     * id, status, run id, consecutive failures, schedule id, …).
     * Signed with an HMAC-SHA256 of the body using `webhookSecret`
     * when present, in the `X-Rynora-Signature` header.
     */
    webhookUrl: z.string().url().optional(),
    webhookSecret: z.string().max(256).optional(),
  })
  .strict();
export type MonitorChannelTargets = z.infer<typeof MonitorChannelTargetsSchema>;

export const MonitorConfigSchema = z
  .object({
    /**
     * Consecutive failures (across runs) before the monitor flips
     * from `up` → `degraded`. Setting to 0 disables the degraded
     * grace window — any failure flips straight to `down`.
     */
    degradedAfterFailures: z.number().int().min(0).max(20).default(1),
    /**
     * Consecutive failures before the monitor pages on-call.
     * Must be ≥ `degradedAfterFailures`. Default 2 (one heads-up
     * + one page).
     */
    pageAfterFailures: z.number().int().min(1).max(20).default(2),
    /**
     * Channels that get paged when the threshold is crossed.
     * v1.1 wires `pagerduty`, `slack`, and `webhook` end-to-end via
     * outbound HTTP — `in_app` + `email` remain the in-process path.
     */
    pageChannels: z.array(z.enum(['in_app', 'email', 'pagerduty', 'slack', 'webhook'])).default(['in_app', 'email']),
    /**
     * v1.1 — per-channel targets. See `MonitorChannelTargetsSchema`.
     * Defaults to `{}` so v1 monitors keep working untouched.
     */
    channelTargets: MonitorChannelTargetsSchema.default({}),
    /**
     * Optional ISO-8601 maintenance windows. Failures during these
     * windows write `status = 'maintenance'` events instead of
     * `down`, and never page.
     */
    maintenanceWindows: z
      .array(
        z.object({
          startsAt: z.string().datetime(),
          endsAt: z.string().datetime(),
          reason: z.string().max(200).optional(),
        }),
      )
      .default([]),
    /**
     * v1.1 — public status-badge slot. When `badge.enabled` is true,
     * the schedule exposes a public SVG badge at
     * `/api/monitors/badge/<token>.svg`. The token is stable across
     * the monitor's lifetime so embed sources don't break on edits.
     * Rotate by patching the monitor with a new token.
     */
    badge: z
      .object({
        enabled: z.boolean().default(false),
        /** 32-character hex token. Generated server-side on first enable. */
        token: z
          .string()
          .regex(/^[a-f0-9]{32}$/)
          .optional(),
      })
      .default({ enabled: false }),
  })
  .strict();
export type MonitorConfig = z.infer<typeof MonitorConfigSchema>;

export const MonitorStatusEventSchema = z.object({
  id: z.string().uuid(),
  scheduleId: z.string().uuid(),
  projectId: z.string().uuid(),
  orgId: z.string().uuid(),
  observedAt: z.string().datetime(),
  status: MonitorStatusSchema,
  runId: z.string().uuid().nullable(),
  consecutiveFailures: z.number().int().min(0),
  durationMs: z.number().int().min(0).nullable(),
  failureReason: z.string().nullable(),
  paged: z.boolean(),
  pagedChannels: z.array(z.string()),
});
export type MonitorStatusEvent = z.infer<typeof MonitorStatusEventSchema>;

export const MonitorSummarySchema = z.object({
  scheduleId: z.string().uuid(),
  projectId: z.string().uuid(),
  name: z.string(),
  cron: z.string(),
  enabled: z.boolean(),
  status: MonitorStatusSchema,
  lastObservedAt: z.string().datetime().nullable(),
  lastRunAt: z.string().datetime().nullable(),
  consecutiveFailures: z.number().int().min(0),
  config: MonitorConfigSchema,
  uptime: z.object({
    last24h: z.number().min(0).max(1),
    last7d: z.number().min(0).max(1),
    last30d: z.number().min(0).max(1),
  }),
});
export type MonitorSummary = z.infer<typeof MonitorSummarySchema>;

export const ListMonitorsResponseSchema = z.object({
  items: z.array(MonitorSummarySchema),
});
export type ListMonitorsResponse = z.infer<typeof ListMonitorsResponseSchema>;

export const ListMonitorEventsQuerySchema = z.object({
  from: z.string().datetime().optional(),
  to: z.string().datetime().optional(),
  limit: z.number().int().min(1).max(1000).optional(),
});
export type ListMonitorEventsQuery = z.infer<typeof ListMonitorEventsQuerySchema>;

export const ListMonitorEventsResponseSchema = z.object({
  items: z.array(MonitorStatusEventSchema),
});
export type ListMonitorEventsResponse = z.infer<typeof ListMonitorEventsResponseSchema>;

export const UpdateMonitorConfigBodySchema = z.object({
  isMonitor: z.boolean().optional(),
  config: MonitorConfigSchema.partial().optional(),
});
export type UpdateMonitorConfigBody = z.infer<typeof UpdateMonitorConfigBodySchema>;

export const MonitorsAdminConfigSchema = z
  .object({
    enabled: z.boolean().default(false),
    defaultConsecutiveFailuresToPage: z.number().int().min(1).max(20).default(2),
    defaultPageChannels: z
      .array(z.enum(['in_app', 'email', 'pagerduty', 'slack', 'webhook']))
      .default(['in_app', 'email']),
    retentionDays: z.number().int().min(7).max(365).default(90),
    /**
     * v1.1 — org-wide channel-target fallbacks. When a monitor's own
     * `channelTargets` slot is empty, the dispatcher falls back here.
     * Empty strings are treated as absent.
     */
    defaultChannelTargets: MonitorChannelTargetsSchema.default({}),
    /**
     * v1.1 — public status-badge gate. When false, the badge route
     * returns 404 regardless of per-monitor toggle; when true, the
     * route honours each monitor's `badge.enabled` flag individually.
     */
    publicBadgesEnabled: z.boolean().default(false),
    /**
     * v1.1 — sliding window per-token rate budget on the badge route
     * to keep abusive embeds from melting the API. Default 60 req/min
     * per token.
     */
    badgeRateLimitPerMin: z.number().int().min(1).max(600).default(60),
  })
  .strict();
export type MonitorsAdminConfig = z.infer<typeof MonitorsAdminConfigSchema>;

/* ============================================================== */
/*  pending-synthetic-monitoring v1.1 — channel-target preview      */
/* ============================================================== */

/**
 * `POST /api/monitors/:scheduleId/channel-test` lets an admin fire
 * a single canned page through one channel target to verify it
 * resolves end-to-end before flipping `enabled = true`.
 */
export const MonitorChannelTestRequestSchema = z.object({
  channel: z.enum(['pagerduty', 'slack', 'webhook']),
});
export type MonitorChannelTestRequest = z.infer<typeof MonitorChannelTestRequestSchema>;

export const MonitorChannelTestResponseSchema = z.object({
  ok: z.boolean(),
  status: z.number().int().min(0).optional(),
  /** Surface the resolved target source: `monitor` | `org-default` | `none`. */
  source: z.enum(['monitor', 'org-default', 'none']),
  message: z.string().optional(),
});
export type MonitorChannelTestResponse = z.infer<typeof MonitorChannelTestResponseSchema>;
